import React, { useState, useEffect, useRef } from 'react';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  deleteNotification 
} from '../../utils/api';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import './NotificationCenter.css';

const NotificationCenter = ({ isOpen, onClose, onNavigate, onNotificationRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userSettings } = useUserSettingsContext();
  const panelRef = useRef(null);

  // Load notifications when panel opens
  useEffect(() => {
    if (isOpen && userSettings?.email) {
      loadNotifications();
    }
  }, [isOpen, userSettings?.email]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Load notifications from API
  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const notificationData = await getUserNotifications(userSettings.email);
      setNotifications(notificationData);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread
      if (!notification.isRead) {
        await markNotificationAsRead(notification.id, userSettings.email);
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        // Notify parent component to refresh unread count
        if (onNotificationRead) {
          onNotificationRead();
        }
      }

      // Navigate to the relevant location
      if (notification.actionUrl && onNavigate) {
        onNavigate(notification.actionUrl, notification);
      }

      // Close the notification center
      onClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userSettings.email);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      // Notify parent component to refresh unread count
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId, event) => {
    event.stopPropagation(); // Prevent click from bubbling to notification click
    
    try {
      await deleteNotification(notificationId, userSettings.email);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'mention': return '💬';
      case 'note_reply': return '↩️';
      case 'report_shared': return '📊';
      case 'system': return '🔔';
      default: return '📢';
    }
  };

  // Group notifications by date
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const date = new Date(notification.createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey;
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'Yesterday';
    } else {
      groupKey = date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(notification);
    return groups;
  }, {});

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (!isOpen) return null;

  return (
    <div className="notification-center-overlay">
      <div className="notification-center" ref={panelRef}>
        {/* Header */}
        <div className="notification-header">
          <div className="notification-title">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="notification-unread-badge">{unreadCount}</span>
            )}
          </div>
          <div className="notification-actions">
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
            <button className="notification-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="notification-content">
          {loading && (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              Loading notifications...
            </div>
          )}

          {error && (
            <div className="notification-error">
              <div className="error-icon">⚠️</div>
              <div className="error-text">{error}</div>
              <button onClick={loadNotifications} className="retry-btn">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="notification-empty">
              <div className="empty-icon">🔔</div>
              <div className="empty-title">No notifications</div>
              <div className="empty-subtitle">You're all caught up!</div>
            </div>
          )}

          {!loading && !error && notifications.length > 0 && (
            <div className="notification-list">
              {Object.entries(groupedNotifications).map(([dateGroup, groupNotifications]) => (
                <div key={dateGroup} className="notification-group">
                  <div className="notification-group-header">{dateGroup}</div>
                  {groupNotifications.map(notification => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-body">
                        <div className="notification-message">
                          <span className="notification-title-text">{notification.title}</span>
                          {notification.message && (
                            <span className="notification-message-text">{notification.message}</span>
                          )}
                        </div>
                        <div className="notification-meta">
                          <span className="notification-time">
                            {formatRelativeTime(notification.createdAt)}
                          </span>
                          {notification.context && (
                            <span className="notification-context">
                              {notification.context.reportName} • {notification.context.pageName}
                            </span>
                          )}
                        </div>
                      </div>
                      <button 
                        className="notification-delete"
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                        title="Delete notification"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;